import { useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import { doMeiliSearch } from '../SearchField/searchService';
import { getMapperFromMeiliResponse } from '../SearchField/mappers';
import { MeiliIndex, SearchDatabaseResult, SearchDatabaseResultItem, SearchItem } from '../SearchField/types';

const QUERY_TYPES = [MeiliIndex.artists, MeiliIndex.tracks, MeiliIndex.playlists];

export interface PaletteResult {
  item: SearchDatabaseResultItem;
  searchItem: SearchItem;
}

export interface PaletteResultSection {
  type: MeiliIndex;
  results: PaletteResult[];
}

/** The artists, tracks and playlists for the search text (2 letters or more), 200 ms after typing. */
export function usePaletteSearch(query: string): { sections: PaletteResultSection[]; isSearching: boolean } {
  const [results, setResults] = useState<SearchDatabaseResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  // Each search gets a number. Only the answer of the newest search shows: an older answer that
  // comes in late (a slow "ab" after "abc") is dropped.
  const latestSearch = useRef(0);

  const search = useMemo(
    () =>
      _.debounce((value: string) => {
        latestSearch.current += 1;
        const searchId = latestSearch.current;
        if (value.trim().length < 2) {
          setResults([]);
          setIsSearching(false);
          return;
        }
        setIsSearching(true);
        doMeiliSearch(value, QUERY_TYPES)
          .then((found) => {
            if (searchId === latestSearch.current && found !== null) {
              setResults(found.filter((section) => section.items.length !== 0));
            }
          })
          .catch(console.error)
          .finally(() => {
            if (searchId === latestSearch.current) {
              setIsSearching(false);
            }
          });
      }, 200),
    []
  );

  useEffect(() => {
    search(query);
  }, [query, search]);

  useEffect(() => () => search.cancel(), [search]);

  const sections = useMemo(
    () =>
      results.map((section) => ({
        type: section.type,
        results: section.items.map((item) => ({
          item,
          searchItem: getMapperFromMeiliResponse(item.type)(item.item as any),
        })),
      })),
    [results]
  );

  return { sections, isSearching };
}
